import { twMerge } from 'tailwind-merge';
import { useMemo, useState, useEffect } from 'react';

import { CourseStepStatus } from 'types';
import { getStepData } from 'utils/steps';
import { useCourseContext } from 'context';
import { useIsTabletDown } from 'hooks/styles';

import { Icon } from '../icons';

import { StepButtonProps } from './types';

export const StepButton = ({ item, onClick }: StepButtonProps) => {
  const [isHovered, setHovered] = useState(false);
  const { getStepStatus } = useCourseContext();
  const isTabletDown = useIsTabletDown();

  const { id, name, color, codeName, coverImage, resourceVersions } = item;
  const { background, stepActiveClassName } = useMemo(
    () => getStepData(color, codeName),
    [color, codeName]
  );

  const status = useMemo(() => getStepStatus(id), [getStepStatus, id]);

  const isResource = resourceVersions && resourceVersions.length > 0;

  const isActive = status === CourseStepStatus.Active;
  const isLocked = status === CourseStepStatus.Locked;
  const isCompleted = status === CourseStepStatus.Completed;

  const isClickable = isActive || (isResource && isCompleted);

  useEffect(() => {
    if (!isClickable && isHovered) {
      setHovered(false);
    }
  }, [isClickable, isHovered]);

  const renderIcon = () => {
    const iconDiv = (
      <img
        alt={name}
        src={coverImage}
        className="relative h-3.5 w-3.5 md:h-6 md:w-6"
      />
    );

    if (isActive) {
      return (
        <>
          <div className="bg-white-primary absolute inset-px rounded-full md:inset-0.5" />
          {iconDiv}
        </>
      );
    }

    if (!isTabletDown) {
      return isCompleted ? (
        <Icon.CheckCircle width={24} height={24} />
      ) : (
        iconDiv
      );
    }

    return null;
  };

  const handleClick = isClickable ? onClick : undefined;
  const handleMouseEnter = isClickable ? () => setHovered(true) : undefined;
  const handleMouseLeave = isClickable ? () => setHovered(false) : undefined;

  return (
    <div
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={isLocked ? { background } : undefined}
      className={twMerge(
        'relative flex h-4 w-4 items-center justify-center rounded-full md:h-10 md:w-10',
        isLocked && 'border-white-primary border-2 opacity-60 md:border-0',
        isCompleted && `${stepActiveClassName} border-white-primary border-2`,
        isActive &&
          `${stepActiveClassName} shadow-greendark-shadow20 h-6 w-6 shadow-[0px_2px_8px_0px]`,
        isClickable && 'cursor-pointer',
        isHovered && 'opacity-80'
      )}
    >
      {renderIcon()}
    </div>
  );
};
